import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
// Material UI components
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	IconButton,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Divider,
} from '@mui/material';
import Alert from '@mui/material/Alert';
// Material UI icons
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
// Local components
import DrugUploadForm from '../../drugUpload';
import { useComponentStyles } from '../../../../hooks/useComponentStyles';

const InventoryTab = ({
	searchTerm = '',
	onExpiringDrugsFound,
	selectedDrug: drugFromNotification,
	onDrugDetailsClosed,
}) => {
	const theme = useTheme();
	const styles = useComponentStyles();
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated = false, userType = null } = useSelector(
		(state) => state.auth || {},
	);
	const [open, setOpen] = useState(false);
	const [drugs, setDrugs] = useState([]);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		severity: 'info',
	});
	const [filterDialogOpen, setFilterDialogOpen] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedDrugDetails, setSelectedDrugDetails] = useState(null);
	const [selectedDrug, setSelectedDrug] = useState(null);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [selectedStatuses, setSelectedStatuses] = useState([]);
	const [availableCategories, setAvailableCategories] = useState([]);
	const [filteredDrugs, setFilteredDrugs] = useState([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [drugToDelete, setDrugToDelete] = useState(null);
	const [currentDrug, setCurrentDrug] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Helper function to check if user can upload
	const canUploadDrug = () => {
		return (
			isAuthenticated && ['doctor', 'hospital', 'pharmacy'].includes(userType)
		);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (
			params.get('openForm') === 'true' &&
			localStorage.getItem('wasUploadingDrug')
		) {
			localStorage.removeItem('wasUploadingDrug');
			setOpen(true);
		}
	}, [location]);

	// Handle button click with auth check
	const handleUploadButtonClick = () => {
		if (!isAuthenticated) {
			localStorage.setItem('wasUploadingDrug', 'true');
			navigate('/app/pharmacy/login');
			return;
		}

		if (canUploadDrug()) {
			handleToggleForm();
		}
	};

	const handleToggleForm = () => {
		setOpen((prevOpen) => {
			if (prevOpen) {
				setIsEditMode(false);
				setCurrentDrug(null);
			}
			return !prevOpen;
		});
	};

	// Fetch drugs on component mount
	useEffect(() => {
		fetchDrugs();

		const handleDrugUpdated = (event) => {
			console.log('Drug updated/added, refreshing inventory immediately');
			fetchDrugs();
		};

		const handleInventoryUpdated = () => {
			console.log('Inventory updated from order status change, refreshing');
			fetchDrugs();
		};

		window.addEventListener('drugUpdated', handleDrugUpdated);
		window.addEventListener('inventoryUpdated', handleInventoryUpdated);

		return () => {
			window.removeEventListener('drugUpdated', handleDrugUpdated);
			window.removeEventListener('inventoryUpdated', handleInventoryUpdated);
		};
	}, []);

	const fetchDrugs = async () => {
		try {
			setLoading(true);

			// Fetch drugs with strong cache busting
			const drugsResponse = await fetch(`/medication/drug/?_=${Date.now()}`, {
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate',
					Pragma: 'no-cache',
				},
			});

			if (!drugsResponse.ok) {
				throw new Error(`Failed to fetch drugs: ${drugsResponse.status}`);
			}

			const drugsData = await drugsResponse.json();

			const activeDrugs = drugsData.filter((drug) => !drug.is_deleted);

			// Fetch orders with cache busting
			const ordersResponse = await fetch(`/medication/orders/?_=${Date.now()}`);
			if (!ordersResponse.ok) {
				throw new Error('Failed to fetch orders');
			}
			const ordersData = await ordersResponse.json();

			// Filter only pending/processing orders that affect inventory
			const relevantOrders = ordersData.filter((order) =>
				['Pending', 'Processing'].includes(order.status),
			);

			// Calculate quantities in pending orders for each drug
			const pendingQuantities = {};
			relevantOrders.forEach((order) => {
				if (order.items && Array.isArray(order.items)) {
					order.items.forEach((item) => {
						if (item.drug && item.drug.id) {
							const drugId = item.drug.id;
							pendingQuantities[drugId] =
								(pendingQuantities[drugId] || 0) + item.quantity;
						}
					});
				}
			});

			// Adjusted stock info to each drug
			const adjustedDrugs = activeDrugs.map((drug) => ({
				...drug,
				totalStock: drug.stock, // Original stock
				pendingStock: pendingQuantities[drug.id] || 0, // Amount in orders
				availableStock: Math.max(
					0,
					drug.stock - (pendingQuantities[drug.id] || 0),
				),
			}));

			console.log('[DEBUG] Setting drugs with adjusted stock');
			setDrugs(adjustedDrugs);
		} catch (err) {
			console.error('[DEBUG] Error fetching data:', err);
			setError('Failed to load inventory data. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Extract unique categories from drugs data
	useEffect(() => {
		if (drugs && drugs.length > 0) {
			const categories = new Set();
			drugs.forEach((drug) => {
				if (drug.categories && drug.categories.length > 0) {
					categories.add(drug.categories[0].name);
				} else {
					categories.add('Uncategorized');
				}
			});
			setAvailableCategories([...categories]);
			setFilteredDrugs(drugs);
		}
	}, [drugs]);

	// Drug details
	const handleRowClick = (event, drug) => {
		if (event.target.closest('.action-buttons')) {
			return;
		}
		setSelectedDrug(drug);
		setDetailsOpen(true);
	};

	// Close details
	const handleCloseDetails = () => {
		setDetailsOpen(false);
		setSelectedDrugDetails(null);

		if (onDrugDetailsClosed) {
			onDrugDetailsClosed();
		}
	};

	// Open details when a drug is passed as prop
	useEffect(() => {
		// Check if a drug was passed from notifications
		if (drugFromNotification) {
			console.log('Drug from notification received:', drugFromNotification);
			setSelectedDrugDetails(drugFromNotification);
			setSelectedDrug(drugFromNotification);
			setDetailsOpen(true);
		}
	}, [drugFromNotification]);

	// Filter functions
	const handleCategoryChange = (category) => {
		setSelectedCategories((prev) =>
			prev.includes(category)
				? prev.filter((cat) => cat !== category)
				: [...prev, category],
		);
	};

	const handleStatusChange = (status) => {
		setSelectedStatuses((prev) =>
			prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status],
		);
	};

	const applyFilters = () => {
		let result = [...drugs];

		if (selectedCategories.length > 0) {
			result = result.filter((drug) => {
				const drugCategory =
					drug.categories && drug.categories.length > 0
						? drug.categories[0].name
						: 'Uncategorized';
				return selectedCategories.includes(drugCategory);
			});
		}

		if (selectedStatuses.length > 0) {
			result = result.filter((drug) => {
				const status = getStockStatus(drug.stock, drug.reorder_level);
				return selectedStatuses.includes(status);
			});
		}

		setFilteredDrugs(result);
		setFilterDialogOpen(false);
	};

	const resetFilters = () => {
		setSelectedCategories([]);
		setSelectedStatuses([]);
		setFilteredDrugs(drugs);
		setFilterDialogOpen(false);
	};

	// Filter drugs when searchTerm changes
	useEffect(() => {
		if (!drugs) return;

		if (!searchTerm || searchTerm.trim() === '') {
			// If no search term, show all drugs
			setFilteredDrugs(drugs);
		} else {
			const term = searchTerm.toLowerCase().trim();

			// Filter drugs based on search term
			const filtered = drugs.filter(
				(drug) =>
					drug.name.toLowerCase().includes(term) ||
					(drug.categories &&
						drug.categories.length > 0 &&
						drug.categories[0].name.toLowerCase().includes(term)),
			);

			setFilteredDrugs(filtered);
		}
	}, [searchTerm, drugs]);

	const handleDownloadDrugs = () => {
		if (!drugs || drugs.length === 0) return;

		try {
			const headers = [
				'Name',
				'Category',
				'Stock',
				'Price',
				'Expiry Date',
				'Status',
			];
			const csvData = drugs.map((drug) => [
				drug.name,
				drug.categories?.length > 0 ? drug.categories[0].name : 'Uncategorized',
				drug.stock,
				drug.price_info
					? `${drug.price_info.user_currency} ${drug.price_info.converted_price}`
					: `KES ${drug.price}`,
				formatExpiryDate(drug.expiration_date),
				getStockStatus(drug.stock, drug.reorder_level),
			]);

			// Create CSV content
			let csvContent = headers.join(',') + '\n';
			csvData.forEach((row) => {
				csvContent += row.join(',') + '\n';
			});

			// Download file
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'inventory.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error('Export failed:', error);
		}
	};

	// Function to determine status based on stock level
	const getStockStatus = (stock, reorderLevel) => {
		if (stock === 0) {
			return 'Out of Stock';
		} else if (stock <= reorderLevel) {
			return 'Low Stock';
		}
		return 'In Stock';
	};

	const getLowStockCount = () => {
		return drugs.filter((drug) => drug.stock <= drug.reorder_level).length;
	};

	// Function to get TOTAL QUANTITY of products with low stock
	const getTotalLowStockQuantity = () => {
		if (!drugs || drugs.length === 0) return 0;

		return drugs
			.filter((drug) => drug.stock <= drug.reorder_level)
			.reduce((total, drug) => total + (parseInt(drug.stock) || 0), 0);
	};
	// date formatting function
	const formatExpiryDate = (isoDate) => {
		if (!isoDate) return 'N/A';
		const date = new Date(isoDate);
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = String(date.getFullYear()).slice(-2);
		return `${day}/${month}/${year}`;
	};

	// Function to get unique categories
	const getUniqueCategories = () => {
		const uniqueCategories = new Set();

		if (!drugs || drugs.length === 0) return 0;

		drugs.forEach((drug) => {
			if (drug.categories && drug.categories.length > 0) {
				drug.categories.forEach((category) => {
					uniqueCategories.add(category.name);
				});
			} else if (drug.category) {
				uniqueCategories.add(drug.category);
			}
		});

		return uniqueCategories.size;
	};

	// Function to count categories added in recent period
	const getCategoriesAddedThisMonth = () => {
		if (!drugs || drugs.length === 0) return 0;

		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
		const recentCategories = new Set();

		drugs.forEach((drug) => {
			if (drug.categories && drug.categories.length > 0) {
				drug.categories.forEach((category) => {
					if (category.created_at) {
						const categoryCreatedDate = new Date(category.created_at);
						if (
							!isNaN(categoryCreatedDate) &&
							categoryCreatedDate >= thirtyDaysAgo
						) {
							recentCategories.add(category.name);
						}
					}
				});
			}
		});

		return recentCategories.size;
	};

	// Calculate total quantity
	const calculateTotalQuantity = () => {
		return drugs
			.reduce((total, drug) => {
				return total + (parseInt(drug.stock) || 0);
			}, 0)
			.toLocaleString();
	};

	// Calculate the total inventory value (Net Purchase Value)
	const calculateTotalInventoryValue = () => {
		return drugs
			.reduce((total, drug) => {
				// Use cost_price instead of selling price
				const costPrice = parseFloat(drug.cost_price || 0);
				const quantity = parseInt(drug.stock || 0);

				// Only add to total if both cost price and quantity are valid
				if (costPrice > 0 && quantity > 0) {
					return total + costPrice * quantity;
				}
				return total;
			}, 0)
			.toLocaleString();
	};

	const handleEditClick = (drug) => {
		setCurrentDrug(drug);
		setIsEditMode(true);
		setOpen(true); // Open the form
	};

	// Function to handle delete button click
	const handleDeleteClick = (drug) => {
		setDrugToDelete(drug);
		setConfirmDelete(true);
	};

	const confirmDeleteDrug = async () => {
		try {
			// Get CSRF token from cookies
			const csrfToken = document.cookie
				.split('; ')
				.find((row) => row.startsWith('csrftoken='))
				?.split('=')[1];

			const response = await fetch(`/medication/drug/${drugToDelete.id}/`, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': csrfToken,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to delete drug: ${response.statusText}`);
			}

			// Update the drugs list by filtering out the deleted drug
			setDrugs((prevDrugs) =>
				prevDrugs.filter((item) => item.id !== drugToDelete.id),
			);

			// Dispatch event to notify other components
			window.dispatchEvent(new Event('drugDeleted'));

			// Show success message
			setSnackbar({
				open: true,
				message: 'Drug deleted successfully',
				severity: 'success',
			});
		} catch (error) {
			console.error('Error deleting drug:', error);
			setSnackbar({
				open: true,
				message: 'Failed to delete drug',
				severity: 'error',
			});
		} finally {
			// Close dialog
			setConfirmDelete(false);
			setDrugToDelete(null);
		}
	};

	// Function to handle cancellation
	const cancelDelete = () => {
		setConfirmDelete(false);
		setDrugToDelete(null);
	};

	return (
		<>
			<Box
				sx={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				{/* Top container */}
				<Box sx={styles.containerBox}>
					<Typography sx={styles.containerTitle}>Overall Inventory</Typography>

					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'row', sm: 'row' },
							flexWrap: { xs: 'wrap', sm: 'nowrap' },
							gap: { xs: 0.5, sm: 1 },
						}}
					>
						{/* Categories */}
						<Box
							sx={{
								...styles.metricContainer,
								flex: { xs: '0 0 calc(50% - 4px)', sm: 1 },
							}}
						>
							<Typography variant="subtitle2" sx={styles.metricTitle}>
								Categories
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
								<Typography
									variant="h5"
									sx={{
										...styles.metricValue,
										...styles.metricValueBlue,
									}}
								>
									{loading ? '...' : getUniqueCategories()}
								</Typography>
							</Box>
							<Typography variant="caption" sx={styles.metricCaption}>
								{loading ? '...' : getCategoriesAddedThisMonth()} added this
								month
							</Typography>
						</Box>

						{/* Total Products */}
						<Box
							sx={{
								...styles.metricContainer,
								flex: { xs: '0 0 calc(50% - 4px)', sm: 1 },
							}}
						>
							<Typography variant="subtitle2" sx={styles.metricTitle}>
								Total Products
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
								<Typography
									variant="h5"
									sx={{
										...styles.metricValue,
										...styles.metricValuePurple,
									}}
								>
									{loading ? '...' : calculateTotalQuantity()}
								</Typography>
							</Box>
							<Typography variant="caption" sx={styles.metricCaption}>
								{`Value: ${loading ? '...' : `KES ${calculateTotalInventoryValue()}`}`}
							</Typography>
						</Box>

						{/* Total Below Threshold */}
						<Box
							sx={{
								...styles.metricContainer,
								flex: { xs: '0 0 calc(50% - 4px)', sm: 1 },
							}}
						>
							<Typography variant="subtitle2" sx={styles.metricTitleWarning}>
								Total Below threshold
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
								<Typography
									variant="h5"
									sx={{
										...styles.metricValue,
										...styles.metricValueStandard,
									}}
								>
									{loading ? '...' : getTotalLowStockQuantity()}
								</Typography>
							</Box>
						</Box>

						{/* Low Stock */}
						<Box
							sx={{
								...styles.metricContainer,
								flex: { xs: '0 0 calc(50% - 4px)', sm: 1 },
								borderRight: 'none',
							}}
						>
							<Typography variant="subtitle2" sx={styles.metricTitleWarning}>
								Low Stock Categories
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
								<Typography
									variant="h5"
									sx={{
										...styles.metricValue,
										...styles.metricValueStandard,
									}}
								>
									{loading ? '...' : getLowStockCount()}
								</Typography>
							</Box>
							<Typography variant="caption" sx={styles.metricCaptionDanger}>
								Needs attention
							</Typography>
						</Box>
					</Box>
				</Box>

				{/* Bottom container with table */}
				<Box
					sx={{
						flex: '1 1 auto',
						bgcolor:
							theme.palette.mode === 'dark'
								? theme.palette.customColors?.cardBackground || '#1E1E2E'
								: '#FFFFFF',
						borderRadius: 2,
						boxShadow:
							theme.palette.mode === 'dark'
								? '0 2px 10px rgba(0,0,0,0.2)'
								: '0 2px 10px rgba(0,0,0,0.05)',
						p: { xs: 1.5, sm: 2 },
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						transition: theme.transitions.create(
							['background-color', 'box-shadow'],
							{
								duration: theme.transitions.duration.standard,
							},
						),
					}}
				>
					{/* Header section */}
					<Box sx={styles.headerBox}>
						<Typography variant="h6" sx={styles.headerTitle}>
							Products
						</Typography>

						{/* Action Buttons */}
						<Box sx={styles.actionButtonsContainer}>
							{/* Upload Button - Only show for authorized user types */}
							{canUploadDrug() && (
								<Button
									variant="contained"
									onClick={handleUploadButtonClick}
									size="small"
									sx={styles.primaryButton}
								>
									{open ? 'Cancel' : 'Upload'}
								</Button>
							)}

							{/* Show login prompt for unauthenticated users */}
							{!isAuthenticated && (
								<Button
									variant="contained"
									onClick={handleUploadButtonClick}
									size="small"
									sx={styles.primaryButton}
								>
									Upload Drug
								</Button>
							)}

							{/* Filter Button */}
							<Button
								variant="outlined"
								startIcon={
									<FilterListIcon
										sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
									/>
								}
								size="small"
								onClick={() => setFilterDialogOpen(true)}
								sx={styles.outlinedBrandButton}
							>
								Filter
								{(selectedCategories.length > 0 ||
									selectedStatuses.length > 0) && (
									<Box component="span" sx={styles.badgeIndicator}>
										{selectedCategories.length + selectedStatuses.length}
									</Box>
								)}
							</Button>

							{/* Filter Dialog */}
							<Dialog
								open={filterDialogOpen}
								onClose={() => setFilterDialogOpen(false)}
								PaperProps={{
									sx: styles.dialogPaper,
								}}
							>
								<DialogTitle sx={styles.title}>Filter Inventory</DialogTitle>
								<DialogContent dividers sx={styles.cardDivider}>
									<Typography
										variant="subtitle1"
										gutterBottom
										sx={styles.title}
									>
										Categories
									</Typography>
									<FormGroup>
										{availableCategories.map((category) => (
											<FormControlLabel
												key={category}
												control={
													<Checkbox
														checked={selectedCategories.includes(category)}
														onChange={() => handleCategoryChange(category)}
														sx={styles.checkbox}
													/>
												}
												label={category}
												sx={styles.checkboxLabel}
											/>
										))}
									</FormGroup>

									<Divider sx={styles.cardDivider} />

									<Typography
										variant="subtitle1"
										gutterBottom
										sx={styles.title}
									>
										Stock Status
									</Typography>
									<FormGroup>
										{['In Stock', 'Low Stock', 'Out of Stock'].map((status) => (
											<FormControlLabel
												key={status}
												control={
													<Checkbox
														checked={selectedStatuses.includes(status)}
														onChange={() => handleStatusChange(status)}
														sx={styles.checkbox}
													/>
												}
												label={status}
												sx={styles.checkboxLabel}
											/>
										))}
									</FormGroup>
								</DialogContent>
								<DialogActions>
									<Button onClick={resetFilters} sx={styles.dialogTextButton}>
										Reset
									</Button>
									<Button
										onClick={applyFilters}
										variant="contained"
										sx={styles.primaryButton}
									>
										Apply
									</Button>
								</DialogActions>
							</Dialog>

							{/* Download Button */}
							<Button
								variant="outlined"
								startIcon={
									<FileDownloadIcon
										sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
									/>
								}
								onClick={handleDownloadDrugs}
								size="small"
								sx={styles.outlinedSuccessButton}
							>
								Download
							</Button>
						</Box>
					</Box>

					{/* Table/cards section */}
					<Box
						sx={{
							...styles.mainScrollContainer,
							minHeight: { xs: '500px', md: '600px' },
							maxHeight: { xs: '500px', md: 'calc(100vh - 250px)' },
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{/* Table for desktop/tablet */}
						<Box
							sx={{
								display: { xs: 'none', md: 'flex' },
								flexDirection: 'column',
								flex: 1,
								overflow: 'hidden',
							}}
						>
							<TableContainer sx={styles.tableContainer}>
								<Table sx={{ minWidth: 650 }}>
									<TableHead>
										<TableRow sx={styles.tableHeaderRow}>
											<TableCell
												sx={{ ...styles.tableHeaderCell, width: '60px' }}
											>
												S/N
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Product Name
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Category
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Quantity
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Unit Price
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Cost Price
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>
												Expiry Date
											</TableCell>
											<TableCell sx={styles.tableHeaderCell}>Status</TableCell>
											<TableCell sx={styles.tableHeaderCell}>Action</TableCell>
										</TableRow>
									</TableHead>
									<TableBody sx={styles.tableBody}>
										{loading ? (
											<TableRow>
												<TableCell colSpan={9} align="center">
													Loading...
												</TableCell>
											</TableRow>
										) : error ? (
											<TableRow>
												<TableCell
													colSpan={9}
													align="center"
													sx={{ color: 'error.main' }}
												>
													{error}
												</TableCell>
											</TableRow>
										) : filteredDrugs.length === 0 ? (
											<TableRow>
												<TableCell
													colSpan={9}
													align="center"
													sx={styles.subtitle}
												>
													No drugs available
												</TableCell>
											</TableRow>
										) : (
											filteredDrugs.map((drug, index) => {
												const status = getStockStatus(
													drug.stock,
													drug.reorder_level,
												);

												return (
													<TableRow
														key={drug.id}
														onClick={(event) => handleRowClick(event, drug)}
														sx={styles.tableRowHover}
													>
														<TableCell>{index + 1}</TableCell>
														<TableCell>{drug.name}</TableCell>
														<TableCell>
															{drug.categories && drug.categories.length > 0
																? drug.categories[0].name
																: 'Uncategorized'}
														</TableCell>
														<TableCell>
															<Box
																sx={{ display: 'flex', alignItems: 'center' }}
															>
																<Typography>{drug.availableStock}</Typography>
																{drug.pendingStock > 0 && (
																	<Tooltip
																		title={`Total: ${drug.totalStock} | In Orders: ${drug.pendingStock} | Available: ${drug.availableStock}`}
																	>
																		<Box
																			component="span"
																			sx={{
																				ml: 0.5,
																				color: styles.subtitle.color,
																				fontSize: '0.75rem',
																			}}
																		>
																			(-{drug.pendingStock})
																		</Box>
																	</Tooltip>
																)}
															</Box>
														</TableCell>
														<TableCell>
															{drug.price_info
																? `${drug.price_info.user_currency} ${drug.price_info.converted_price}`
																: `KES ${drug.price}`}
														</TableCell>
														<TableCell>
															{drug.cost_price
																? `${drug.currency || 'KES'} ${drug.cost_price}`
																: 'N/A'}
														</TableCell>
														<TableCell>
															{formatExpiryDate(drug.expiration_date)}
														</TableCell>
														<TableCell>
															<Box sx={styles.stockStatusIndicator(status)}>
																{/* Stock status indicator */}
																{(() => {
																	switch (status) {
																		case 'Out of Stock':
																			return (
																				<>
																					<CloseIcon
																						sx={{ fontSize: '0.875rem' }}
																					/>
																					{status}
																				</>
																			);
																		case 'Low Stock':
																			return (
																				<>
																					<WarningIcon
																						sx={{ fontSize: '0.875rem' }}
																					/>
																					{status}
																				</>
																			);
																		default:
																			return (
																				<>
																					<CheckIcon
																						sx={{ fontSize: '0.875rem' }}
																					/>
																					{status}
																				</>
																			);
																	}
																})()}
															</Box>
														</TableCell>
														<TableCell>
															<Box sx={{ display: 'flex' }}>
																<Tooltip title="Edit">
																	<IconButton
																		size="small"
																		sx={styles.actionButton.brandColor}
																		onClick={(event) => {
																			event.stopPropagation();
																			handleEditClick(drug);
																		}}
																	>
																		<EditIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Delete">
																	<IconButton
																		size="small"
																		sx={styles.actionButton.danger}
																		onClick={(event) => {
																			event.stopPropagation();
																			handleDeleteClick(drug);
																		}}
																	>
																		<DeleteOutlineIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															</Box>
														</TableCell>
													</TableRow>
												);
											})
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>

						{/* Card view for mobile */}
						<Box sx={{ display: { xs: 'block', md: 'none' } }}>
							{loading ? (
								<Box sx={styles.loadingContainer}>
									<CircularProgress
										size={24}
										sx={{ color: styles.metricValueBlue.color }}
									/>
									<Typography sx={{ mt: 1, ...styles.subtitle }}>
										Loading...
									</Typography>
								</Box>
							) : error ? (
								<Box sx={{ ...styles.loadingContainer, color: 'error.main' }}>
									{error}
								</Box>
							) : filteredDrugs.length === 0 ? (
								<Box sx={styles.loadingContainer}>
									<Typography sx={styles.subtitle}>
										No drugs available
									</Typography>
								</Box>
							) : (
								filteredDrugs.map((drug, index) => {
									const status = getStockStatus(drug.stock, drug.reorder_level);

									return (
										<Box
											key={drug.id}
											onClick={(event) => handleRowClick(event, drug)}
											sx={styles.mobileCard}
										>
											{/* Serial Number Badge */}
											<Box sx={styles.serialNumberBadge}>{index + 1}</Box>

											<Box sx={styles.mobileCardHeader}>
												<Typography sx={styles.mobileCardTitle}>
													{drug.name}
												</Typography>
												<Box sx={styles.mobileStatusIndicator(status)}>
													{status === 'Out of Stock' && (
														<CloseIcon sx={{ fontSize: '0.75rem' }} />
													)}
													{status === 'Low Stock' && (
														<WarningIcon sx={{ fontSize: '0.75rem' }} />
													)}
													{status === 'In Stock' && (
														<CheckIcon sx={{ fontSize: '0.75rem' }} />
													)}
													<Typography sx={{ fontSize: '0.65rem', ml: 0.25 }}>
														{status}
													</Typography>
												</Box>
											</Box>

											<Box sx={styles.mobileCardContent}>
												<Box>
													<Typography sx={styles.infoLabel}>
														Category
													</Typography>
													<Typography sx={styles.infoValue}>
														{drug.categories && drug.categories.length > 0
															? drug.categories[0].name
															: 'Uncategorized'}
													</Typography>
												</Box>

												<Box>
													<Typography sx={styles.infoLabel}>
														Quantity
													</Typography>
													<Typography sx={styles.infoValue}>
														{drug.stock}
													</Typography>
												</Box>

												<Box>
													<Typography sx={styles.infoLabel}>
														Unit Price
													</Typography>
													<Typography sx={styles.infoValue}>
														{drug.price_info
															? `${drug.price_info.user_currency} ${drug.price_info.converted_price}`
															: `KES ${drug.price}`}
													</Typography>
												</Box>

												<Box>
													<Typography sx={styles.infoLabel}>Expires</Typography>
													<Typography sx={styles.infoValue}>
														{formatExpiryDate(drug.expiration_date)}
													</Typography>
												</Box>
											</Box>

											<Box sx={styles.mobileCardFooter}>
												<IconButton
													size="small"
													sx={styles.actionButton.brandColor}
													onClick={(event) => {
														event.stopPropagation();
														handleEditClick(drug);
													}}
												>
													<EditIcon sx={{ fontSize: '1rem' }} />
												</IconButton>
												<IconButton
													size="small"
													sx={styles.actionButton.danger}
													onClick={(event) => {
														event.stopPropagation();
														handleDeleteClick(drug);
													}}
												>
													<DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
												</IconButton>
											</Box>
										</Box>
									);
								})
							)}
						</Box>
					</Box>
				</Box>
			</Box>
			{/* Deletion */}
			<Dialog
				open={confirmDelete}
				onClose={cancelDelete}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Are you sure you want to delete "{drugToDelete?.name}"? This action
						cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelDelete} color="primary">
						Cancel
					</Button>
					<Button onClick={confirmDeleteDrug} color="error" autoFocus>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			{/* DrugUpload */}
			<Dialog
				open={open}
				onClose={handleToggleForm}
				maxWidth="md"
				fullWidth
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: 2,
						boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
					},
				}}
			>
				<DrugUploadForm
					open={open}
					handleClose={handleToggleForm}
					isEditMode={isEditMode}
					drugData={currentDrug}
					onSubmit={() => {
						// Refresh drug list
						fetchDrugs();
					}}
				/>
			</Dialog>

			{/* Drug Details*/}
			<Dialog
				open={detailsOpen}
				onClose={handleCloseDetails}
				maxWidth="md"
				fullWidth
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: 2,
						boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
					},
				}}
			>
				{selectedDrug && (
					<>
						<DialogTitle sx={{ borderBottom: '1px solid #F3F4F6', pb: 2 }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<Typography variant="h6">{selectedDrug.name}</Typography>
								<IconButton onClick={handleCloseDetails}>
									<CloseIcon />
								</IconButton>
							</Box>
						</DialogTitle>
						<DialogContent sx={{ pt: 3 }}>
							<Grid container spacing={3}>
								{/* Basic Information */}
								<Grid item xs={12} md={6}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										Basic Information
									</Typography>
									<Box sx={{ mt: 1 }}>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Category:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.categories &&
												selectedDrug.categories.length > 0
													? selectedDrug.categories[0].name
													: 'Uncategorized'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Unit Price:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.price_info
													? `${selectedDrug.price_info.user_currency} ${selectedDrug.price_info.converted_price}`
													: `KES ${selectedDrug.price}`}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Cost Price:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.cost_price
													? `${selectedDrug.currency || 'KES'} ${selectedDrug.cost_price}`
													: 'N/A'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Expiry Date:
											</Typography>
											<Typography variant="body2">
												{formatExpiryDate(selectedDrug.expiration_date)}
											</Typography>
										</Box>
									</Box>
								</Grid>

								{/* Inventory Details */}
								<Grid item xs={12} md={6}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										Inventory Details
									</Typography>
									<Box sx={{ mt: 1 }}>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Available Stock:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.availableStock}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Total Stock:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.totalStock}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												In Orders:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.pendingStock}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Reorder Level:
											</Typography>
											<Typography variant="body2">
												{selectedDrug.reorder_level}
											</Typography>
										</Box>
									</Box>
								</Grid>

								{/* Drug Description */}
								{selectedDrug.description && (
									<Grid item xs={12}>
										<Typography
											variant="subtitle1"
											fontWeight="bold"
											gutterBottom
										>
											Description
										</Typography>
										<Typography variant="body2" sx={{ mt: 1 }}>
											{selectedDrug.description}
										</Typography>
									</Grid>
								)}
							</Grid>
						</DialogContent>
						<DialogActions sx={{ borderTop: '1px solid #F3F4F6', p: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleCloseDetails}
							>
								Close
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</>
	);
};

export default InventoryTab;
